import type { GQL_ID, CommonSort } from '@@partner:types:graphql'
import type { ProductListItem, ProductPrice, ProductStock, Shop } from '@@partner:types:app'
import type { SortType } from 'rsuite-table'

import React, { useMemo } from 'react'

import { Button, Avatar, Tag, Checkbox } from 'rsuite'
import { Link } from 'react-router-dom'
import CommonStatusLabel from '@components/common/CommonStatusLabel'
import ResponsiveTable, { ResponsiveTableColumn } from '@components/common/ResponsiveTable'

import { SORT_ORDER_DESC } from '@@partner:config/graphql'
import { NO_PHOTO_IMAGE } from '@@partner:config/image'
import { formatAmountPostfix } from '@@partner:helpers/product'

export type showSettingsType = {
  shopsIds: GQL_ID[]
  shops?: Shop[]
}

export interface ProductsTableProps {
  tableSort: CommonSort
  loading: boolean
  products: ProductListItem[]
  generateEditUrl?: (productId: GQL_ID) => string
  onSortColumn: (column: string, sortType?: SortType) => void
  showSettings: showSettingsType
  selected: GQL_ID[]
  onSelect: (product_id: GQL_ID) => void
  renderToolbar?: () => React.ReactNode
  total?: number
}

export default function ProductsTable ({
  tableSort,
  loading = false,
  products = [],
  generateEditUrl,
  onSortColumn,
  selected,
  onSelect,
  showSettings,
  renderToolbar,
  total
}: ProductsTableProps) {
  const columns = useMemo<ResponsiveTableColumn<ProductListItem>[]>(() => {
    const cols: ResponsiveTableColumn<ProductListItem>[] = [
      {
        dataKey: 'selected',
        header: 'Выбрать',
        width: 50,
        hideOnMobile: true,
        render: (product) => (
          <Checkbox
            checked={selected.includes(product.id)}
            onChange={() => onSelect(product.id)}
          />
        )
      },
      {
        dataKey: 'preview',
        header: 'Фото',
        width: 80,
        hideOnMobile: true,
        render: (product) => (
          <Avatar src={product?.preview?.url ?? NO_PHOTO_IMAGE} size="md" />
        )
      },
      {
        dataKey: 'id',
        header: 'ID',
        sortable: true,
        flexGrow: 1,
        isCardTitle: true,
        render: (product) => (
          <div>
            <div>ID: {product.id}</div>
            <div>арт. {product.article_num ?? product.xid}</div>
          </div>
        ),
        renderMobile: (product) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Avatar src={product?.preview?.url ?? NO_PHOTO_IMAGE} size="sm" />
            <div>
              <div><strong>{product.name}</strong></div>
              <div style={{ fontSize: '12px', color: '#888' }}>
                ID: {product.id} | арт. {product.article_num ?? product.xid}
              </div>
            </div>
          </div>
        )
      },
      {
        dataKey: 'name',
        header: 'Название',
        sortable: true,
        flexGrow: 3,
        hideOnMobile: true
      },
      {
        dataKey: 'category',
        header: 'Категории',
        flexGrow: 2,
        render: (product) => (
          <div>
            {product.categories.map(category => (
              <div key={category.id}>{category.name}</div>
            ))}
          </div>
        )
      },
      {
        dataKey: 'status',
        header: 'Статус',
        sortable: true,
        flexGrow: 1,
        isCardBadge: true,
        render: (product) => <CommonStatusLabel status={product.status} />
      },
      {
        dataKey: 'indexable',
        header: 'Виден покупателю',
        width: 110,
        hideOnMobile: true,
        render: (product) => (
          <Tag color={product.indexable ? 'green' : 'red'}>
            {product.indexable ? 'Да' : 'Нет'}
          </Tag>
        )
      }
    ]

    // Добавляем колонки цен и остатков если выбраны магазины
    if (showSettings.shopsIds.length > 0) {
      cols.push({
        dataKey: 'price',
        header: 'Цена',
        sortable: true,
        flexGrow: 2,
        render: (product) => (
          <div>
            {product?.allPrices
              .filter(price => showSettings.shopsIds.includes(price.shop_id))
              .map((productPrice: ProductPrice) => (
                <div key={productPrice.shop_id} style={{ marginBottom: '0.5em' }}>
                  <Tag
                    color={productPrice.salePeriod.start ? 'orange' : 'green'}
                    style={{ minWidth: '70px', textAlign: 'center' }}
                  >
                    {productPrice.currentPrice} р.
                  </Tag>
                  <Tag color={undefined}>{productPrice?.shop?.name}</Tag>
                </div>
              ))}
          </div>
        )
      })

      cols.push({
        dataKey: 'stock',
        header: 'Остаток',
        sortable: true,
        flexGrow: 2,
        render: (product) => (
          <div>
            {product?.allStocks
              .filter(stock => showSettings.shopsIds.includes(stock.shop_id))
              .map((productStock: ProductStock) => (
                <div key={productStock.shop_id} style={{ marginBottom: '0.5em' }}>
                  <Tag
                    color="green"
                    style={{ minWidth: '70px', textAlign: 'center' }}
                  >
                    {productStock?.amount} {formatAmountPostfix(product.price_type)}
                  </Tag>
                  <Tag color={undefined}>
                    {showSettings.shops?.find(shop => shop.id === productStock.shop_id)?.name}
                  </Tag>
                </div>
              ))}
          </div>
        )
      })
    }

    return cols
  }, [ selected, onSelect, showSettings ])

  const renderActions = useMemo(() => {
    if (!generateEditUrl) return undefined
    
    return (product: ProductListItem) => (
      <Button
        appearance="primary"
        as={Link}
        to={generateEditUrl(product.id as GQL_ID)}
        size="xs"
      >
        Изменить
      </Button>
    )
  }, [ generateEditUrl ])

  return (
    <ResponsiveTable
      data={products}
      columns={columns}
      loading={loading}
      rowKey="id"
      sortColumn={tableSort.fieldName}
      sortType={tableSort.direction === SORT_ORDER_DESC ? 'desc' : 'asc'}
      onSortColumn={onSortColumn}
      renderActions={renderActions}
      emptyMessage="Товары не найдены"
      storageKey="products-table"
      renderToolbar={renderToolbar}
      total={total}
      totalLabel="Найдено товаров"
    />
  )
}
